import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Box,
	Typography,
	Card,
	CardContent,
	CardActions,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Button,
	CircularProgress,
	Chip,
	Container,
	Paper,
	Divider,
	TextField,
	Grid,
	Avatar,
	Switch,
	FormControlLabel
} from '@mui/material'
import { 
	History as HistoryIcon, 
	TrendingUp, 
	TrendingDown, 
	SwapHoriz,
	Warning,
	Add as AddIcon
} from '@mui/icons-material'
import { useBatch } from '../hooks/useBatch'
import { useCrop } from '../hooks/useCrop'
import { useCategory } from '../hooks/useCategory'
import { MovementHistoryFilters } from '../models/batch'

interface Movement {
	_id: string;
	batchId: string;
	crop: string;
	germplasm: string;
	category: string;
	owner: string;
	history: {
		date: string;
		action: string;
		mount: number;
		user: string;
		notes?: string;
		sale?: string;
		donation?: string;
		transfer?: string;
		outcomeNumber?: number;
	};
}

export const MovementHistory: React.FC = () => {
	const { generateMovementHistory, loadingReport } = useBatch()
	const { crops } = useCrop()
	const { categories } = useCategory()
	const navigate = useNavigate()

	const [filters, setFilters] = useState<MovementHistoryFilters>({})
	const [movements, setMovements] = useState<Movement[]>([])
	const [total, setTotal] = useState(0)
	const [selectedCrop, setSelectedCrop] = useState('')
	const [availableGermplasms, setAvailableGermplasms] = useState<any[]>([])
	const [includeInactive, setIncludeInactive] = useState(false)

	// Update germplasm options when crop changes
	useEffect(() => {
		if (selectedCrop) {
			const crop = crops.find(c => c.name === selectedCrop)
			setAvailableGermplasms(crop?.germplasm || [])
			setFilters(prev => ({ ...prev, germplasm: undefined }))
		} else {
			setAvailableGermplasms([])
		}
	}, [selectedCrop, crops])

	const handleFilterChange = (field: keyof MovementHistoryFilters, value: string | undefined) => {
		if (field === 'crop') {
			setSelectedCrop(value || '')
		}
		setFilters(prev => ({
			...prev,
			[field]: value === '' ? undefined : value
		}))
	}

	const handleGenerateReport = async () => {
		try {
			const reportFilters = {
				...filters,
				...(includeInactive && { includeInactive: 'true' })
			}
			const response = await generateMovementHistory(reportFilters)
			if (response) {
				setMovements(response.movements || [])
				setTotal(response.total || 0)
			}
		} catch (error) {
			console.error('Error generating movement history:', error)
		}
	}

	const handleResetFilters = () => {
		setFilters({})
		setSelectedCrop('')
		setIncludeInactive(false)
		setMovements([])
		setTotal(0)
	}

	const handleManageBatch = (batchId: string) => {
		navigate('/home/administrar-lote', { state: { batchId } })
	}

	const hasActiveFilters = Object.values(filters).some(value => value !== undefined)

	const getActionIcon = (action: string) => {
		switch (action) {
			case 'ingreso':
				return <AddIcon sx={{ color: '#4caf50' }} />
			case 'sale':
				return <TrendingDown sx={{ color: '#2196f3' }} />
			case 'donation':
				return <TrendingDown sx={{ color: '#ff9800' }} />
			case 'transfer':
				return <SwapHoriz sx={{ color: '#9c27b0' }} />
			case 'discard':
				return <Warning sx={{ color: '#f44336' }} />
			default:
				return <HistoryIcon sx={{ color: '#757575' }} />
		}
	}

	const getActionLabel = (action: string) => {
		switch (action) {
			case 'ingreso':
				return 'Ingreso'
			case 'sale':
				return 'Venta'
			case 'donation':
				return 'Donación'
			case 'transfer':
				return 'Transferencia'
			case 'discard':
				return 'Descarte'
			default:
				return action.charAt(0).toUpperCase() + action.slice(1)
		}
	}

	const getActionColor = (action: string) => {
		switch (action) {
			case 'ingreso':
				return '#e8f5e8'
			case 'sale':
				return '#e3f2fd'
			case 'donation':
				return '#fff3e0'
			case 'transfer':
				return '#f3e5f5'
			case 'discard':
				return '#ffebee'
			default:
				return '#f5f5f5'
		}
	}

	const formatNumber = (num: number) => {
		return new Intl.NumberFormat('es-GT', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(num)
	}

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
				<Box sx={{ mb: 4 }}>
					<Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
						Historial de Movimientos
					</Typography>
					<Typography variant="subtitle1" color="text.secondary">
						Consulta el historial completo de movimientos de lotes de semillas
					</Typography>
				</Box>

				{/* Filter Section */}
				<Paper elevation={2} sx={{ p: 3, mb: 4 }}>
					<Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<HistoryIcon color="primary" />
						Filtros de Búsqueda
					</Typography>
					<Divider sx={{ mb: 3 }} />
					
					<Grid container spacing={3} mb={3}>
						<Grid item xs={12} sm={6} md={2.4}>
							<FormControl fullWidth>
								<InputLabel>Cultivo</InputLabel>
								<Select
									value={filters.crop || ''}
									onChange={(e) => handleFilterChange('crop', e.target.value)}
									label="Cultivo"
								>
									<MenuItem value="">Todos</MenuItem>
									{crops.map((crop) => (
										<MenuItem key={crop.uid} value={crop.name}>
											{crop.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={2.4}>
							<FormControl fullWidth disabled={!selectedCrop}>
								<InputLabel>Germoplasma</InputLabel>
								<Select
									value={filters.germplasm || ''}
									onChange={(e) => handleFilterChange('germplasm', e.target.value)}
									label="Germoplasma"
								>
									<MenuItem value="">Todos</MenuItem>
									{availableGermplasms.map((germplasm) => (
										<MenuItem key={germplasm.name} value={germplasm.name}>
											{germplasm.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={2.4}>
							<FormControl fullWidth>
								<InputLabel>Categoría</InputLabel>
								<Select
									value={filters.category || ''}
									onChange={(e) => handleFilterChange('category', e.target.value)}
									label="Categoría"
								>
									<MenuItem value="">Todas</MenuItem>
									{categories.map((category) => (
										<MenuItem key={category.uid} value={category.name}>
											{category.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={2.4}>
							<TextField
								fullWidth
								label="No. de Lote"
								placeholder="Buscar por ID..."
								value={filters.batchId || ''}
								onChange={(e) => handleFilterChange('batchId', e.target.value)}
								variant="outlined"
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={2.4}>
							<TextField
								fullWidth
								label="Fecha Inicio"
								type="date"
								value={filters.startDate || ''}
								onChange={(e) => handleFilterChange('startDate', e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
					</Grid>

					<Grid container spacing={3} mb={2}>
						<Grid item xs={12} sm={6} md={2.4}>
							<TextField
								fullWidth
								label="Fecha Fin"
								type="date"
								value={filters.endDate || ''}
								onChange={(e) => handleFilterChange('endDate', e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
					</Grid>

					{/* Action Buttons */}
					<Box display="flex" gap={2} justifyContent="center" mb={2}>
						<Button
							variant="contained"
							onClick={handleGenerateReport}
							disabled={loadingReport}
							sx={{ minWidth: '150px' }}
							startIcon={loadingReport ? <CircularProgress size={20} color="inherit" /> : <HistoryIcon />}
						>
							{loadingReport ? 'Generando...' : 'Generar Reporte'}
						</Button>
						{hasActiveFilters && (
							<Button
								variant="outlined"
								onClick={handleResetFilters}
							>
								Limpiar Filtros
							</Button>
						)}
					</Box>

					<Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
						<FormControlLabel
							control={
								<Switch
									checked={includeInactive}
									onChange={(e) => setIncludeInactive(e.target.checked)}
									color="primary"
								/>
							}
							label="Incluir lotes desactivados"
						/>
					</Box>

					{/* Active Filters */}
					{hasActiveFilters && (
						<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
							{filters.crop && (
								<Chip
									label={`Cultivo: ${filters.crop}`}
									size="small"
									color="primary"
									variant="outlined"
								/>
							)}
							{filters.germplasm && (
								<Chip
									label={`Germoplasma: ${filters.germplasm}`}
									size="small"
									color="primary"
									variant="outlined"
								/>
							)}
							{filters.category && (
								<Chip
									label={`Categoría: ${filters.category}`}
									size="small"
									color="primary"
									variant="outlined"
								/>
							)}
							{filters.batchId && (
								<Chip
									label={`Lote: ${filters.batchId}`}
									size="small"
									color="primary"
									variant="outlined"
								/>
							)}
							{filters.startDate && (
								<Chip
									label={`Desde: ${new Date(filters.startDate).toLocaleDateString('es-GT')}`}
									size="small"
									color="primary"
									variant="outlined"
								/>
							)}
							{filters.endDate && (
								<Chip
									label={`Hasta: ${new Date(filters.endDate).toLocaleDateString('es-GT')}`}
									size="small"
									color="primary"
									variant="outlined"
								/>
							)}
						</Box>
					)}
				</Paper>

				{/* Results Section */}
				{movements.length > 0 && (
					<Box>
						<Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<HistoryIcon color="primary" />
							Movimientos Encontrados ({total})
						</Typography>
						
						<Grid container spacing={2}>
							{movements.map((movement, index) => (
								<Grid item xs={12} sm={6} md={4} key={`${movement._id}-${index}`}>
									<Card 
										sx={{ 
											height: '100%',
											bgcolor: getActionColor(movement.history.action),
											border: `1px solid ${getActionColor(movement.history.action)}`,
											'&:hover': {
												boxShadow: 4,
												transform: 'translateY(-2px)',
												transition: 'all 0.3s ease-in-out'
											}
										}}
									>
										<CardContent>
											<Box display="flex" alignItems="center" mb={2}>
												<Avatar sx={{ bgcolor: 'white', mr: 2 }}>
													{getActionIcon(movement.history.action)}
												</Avatar>
												<Box>
													<Typography variant="h6" component="h3">
														{getActionLabel(movement.history.action)}
													</Typography>
													<Typography variant="body2" color="textSecondary">
														{new Date(movement.history.date).toLocaleDateString('es-GT')}
													</Typography>
												</Box>
											</Box>

											<Divider sx={{ mb: 2 }} />

											<Typography variant="body2" mb={1}>
												<strong>Lote:</strong> {movement.batchId}
											</Typography>
											<Typography variant="body2" mb={1}>
												<strong>Cultivo:</strong> {movement.crop}
											</Typography>
											<Typography variant="body2" mb={1}>
												<strong>Germoplasma:</strong> {movement.germplasm}
											</Typography>
											<Typography variant="body2" mb={1}>
												<strong>Categoría:</strong> {movement.category}
											</Typography>
											<Typography variant="body2" mb={1}>
												<strong>Cantidad:</strong> {formatNumber(movement.history.mount)} qq
											</Typography>
											<Typography variant="body2" mb={1}>
												<strong>Usuario:</strong> {movement.history.user}
											</Typography>
											{movement.history.notes && (
												<Typography variant="body2" mb={1}>
													<strong>Notas:</strong> {movement.history.notes}
												</Typography>
											)}
										</CardContent>
										<CardActions sx={{ justifyContent: 'center', pb: 2 }}>
											<Button
												variant="contained"
												size="small"
												onClick={() => handleManageBatch(movement._id)}
												sx={{ minWidth: '140px' }}
											>
												Ver Lote
											</Button>
										</CardActions>
									</Card>
								</Grid>
							))}
						</Grid>
					</Box>
				)}

				{/* Empty State */}
				{movements.length === 0 && !loadingReport && (
					<Paper 
						elevation={1} 
						sx={{ 
							p: 6, 
							textAlign: 'center', 
							background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)'
						}}
					>
						<HistoryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
						<Typography variant="h6" color="text.secondary" gutterBottom>
							Generar Historial de Movimientos
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
							Configura los filtros y presiona "Generar Reporte" para visualizar el historial de movimientos
						</Typography>
					</Paper>
				)}
			</Container>
	)
}
